import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { TimeRequestOptions, timeEntriesAPI, timeLookupsAPI, timeProfilesAPI } from '../services/api';
import { TimeCustomerLookup, TimeEntry, TimeProfile, TimeProjectLookup, TimeProjectTaskLookup, TimeSummaryReport, PersonalDashboardRead } from '../types';
import { formatDate, formatDurationMinutes, minutesToParts, partsToMinutes, todayIso } from '../utils';
import { resolveEntryProfileId } from '../utils/entryProfile';
import DateInput from './DateInput';
import TimeSelect from './TimeSelect';
import CopyDayDialog, { copyDayFeedback } from './CopyDayDialog';
import { useCopyDayOperations } from '../hooks/useCopyDayOperations';
import { authAccountFromToken } from '../services/authAccount';

interface DashboardEntryFormState {
  timeProfileId: number;
  customerId: number;
  customerQuery: string;
  projectId: number;
  projectQuery: string;
  projectTaskId: number;
  projectTaskQuery: string;
  workDate: string;
  hours: number | '';
  minutes: number | '';
  isBillable: boolean;
  description: string;
}

const defaultEntryForm: DashboardEntryFormState = {
  timeProfileId: 0,
  customerId: 0,
  customerQuery: '',
  projectId: 0,
  projectQuery: '',
  projectTaskId: 0,
  projectTaskQuery: '',
  workDate: todayIso(),
  hours: '',
  minutes: '',
  isBillable: true,
  description: '',
};

const emptySummary: TimeSummaryReport = {
  startDate: todayIso(),
  endDate: todayIso(),
  entryCount: 0,
  profileCount: 0,
  totalMinutes: 0,
  totalHours: 0,
  billableMinutes: 0,
  billableHours: 0,
  nonBillableMinutes: 0,
  nonBillableHours: 0,
  byProfile: [],
  byProject: [],
  byCustomer: [],
  byBillableStatus: [],
};

const emptyDashboard: PersonalDashboardRead = { startDate: '', endDate: '', items: [], totalCount: 0, summary: emptySummary, sourceDays: [] };

const toLocalDate = (isoDate: string) => {
  const [year, month, day] = isoDate.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDaysIso = (isoDate: string, days: number) => {
  const date = toLocalDate(isoDate);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
};

const startOfDashboardWeekIso = (date = new Date()) => {
  const copy = new Date(date);
  const day = copy.getDay();
  copy.setDate(copy.getDate() - day + (day === 0 ? -6 : 1));
  return toIsoDate(copy);
};

const getDashboardWeekRange = (currentWeekStart: string, weekOffset: number) => {
  const startDate = addDaysIso(currentWeekStart, -7 * weekOffset);
  return {
    startDate,
    endDate: addDaysIso(startDate, 6),
  };
};

const formatWeekday = (isoDate: string) => toLocalDate(isoDate).toLocaleDateString(undefined, {
  weekday: 'long',
});

const Dashboard: React.FC = () => {
  const { actualIsAdmin, user, token } = useAuth();
  const accountKey = authAccountFromToken(token);
  const [dashboardModel, setDashboardModel] = useState<{ accountKey: string | null; data: PersonalDashboardRead }>({ accountKey, data: emptyDashboard });
  const dashboard = dashboardModel.accountKey === accountKey ? dashboardModel.data : emptyDashboard;
  const { summary, items: weekEntries, sourceDays } = dashboard;
  const [copySelection, setCopySelection] = useState<{ sourceDate: string; operationId?: string; accountKey: string | null } | null>(null);
  const readGeneration = useRef(0);
  const [profiles, setProfiles] = useState<TimeProfile[]>([]);
  const [customers, setCustomers] = useState<TimeCustomerLookup[]>([]);
  const [projects, setProjects] = useState<TimeProjectLookup[]>([]);
  const [projectTasks, setProjectTasks] = useState<TimeProjectTaskLookup[]>([]);
  const [isEntryFormOpen, setEntryFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<TimeEntry | null>(null);
  const [entryForm, setEntryForm] = useState<DashboardEntryFormState>(defaultEntryForm);
  const [entryAccountKey, setEntryAccountKey] = useState(accountKey);
  const [loading, setLoading] = useState(true);
  const [entrySaving, setEntrySaving] = useState(false);
  const [error, setError] = useState('');
  const [loadedWeekCount, setLoadedWeekCount] = useState(2);

  useEffect(() => {
    setEntryAccountKey(accountKey);
    setEntryFormOpen(false);
    setEditingEntry(null);
    setEntryToDelete(null);
    setEntryForm(defaultEntryForm);
  }, [accountKey]);

  const currentWeekStart = useMemo(() => startOfDashboardWeekIso(), []);
  const dashboardStartDate = useMemo(() => addDaysIso(currentWeekStart, -7 * (loadedWeekCount - 1)), [currentWeekStart, loadedWeekCount]);
  const dashboardEndDate = useMemo(() => addDaysIso(currentWeekStart, 6), [currentWeekStart]);

  // Update the whole read model together; stale ranges/accounts never replace it.
  const currentRange = useRef({ dashboardStartDate, dashboardEndDate, accountKey });
  currentRange.current = { dashboardStartDate, dashboardEndDate, accountKey };
  const loadDashboard = useCallback(async (): Promise<boolean> => {
    const generation = ++readGeneration.current;
    const range = { startDate: dashboardStartDate, endDate: dashboardEndDate };
    const isCurrent = () => generation === readGeneration.current &&
      currentRange.current.dashboardStartDate === range.startDate && currentRange.current.dashboardEndDate === range.endDate &&
      currentRange.current.accountKey === accountKey;
    setLoading(true);
    setError('');
    try {
      const data = await timeEntriesAPI.getDashboard(range, accountKey || undefined);
      if (!isCurrent()) return false;
      if (data.startDate !== range.startDate || data.endDate !== range.endDate || data.totalCount !== data.items.length) {
        throw new Error('Incomplete or mismatched Dashboard response');
      }
      setDashboardModel({ accountKey, data });
      return true;
    } catch {
      if (isCurrent()) setError('Dashboard data could not be loaded');
      return false;
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [accountKey, dashboardEndDate, dashboardStartDate]);

  const invalidateDashboardReads = useCallback(() => { ++readGeneration.current; }, []);
  useEffect(() => {
    void loadDashboard();
    return invalidateDashboardReads;
  }, [loadDashboard, invalidateDashboardReads]);

  const copyDays = useCopyDayOperations(accountKey, loadDashboard);
  const selectedCopy = copySelection?.accountKey === accountKey ? copySelection : null;
  const secondaryDialog = useRef<HTMLElement>(null);
  const activeSecondaryDialog = !selectedCopy && entryAccountKey === accountKey
    ? entryToDelete ? 'delete' : isEntryFormOpen ? 'entry' : null : null;
  useEffect(() => {
    const modal = secondaryDialog.current;
    if (!activeSecondaryDialog || !modal) return;
    const controls = () => Array.from(modal.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled):not([tabindex="-1"]), textarea:not(:disabled), select:not(:disabled)'
    ));
    // Copy dismissal remounts a suspended editor/confirmation. Restore focus inside
    // it after the copy dialog cleanup, rather than behind the resumed overlay.
    const focusFirst = () => (controls()[0] || modal).focus();
    focusFirst();
    const keepFocus = (event: FocusEvent) => {
      if (!modal.contains(event.target as Node)) focusFirst();
    };
    const trapTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const list = controls(); const first = list[0]; const last = list[list.length - 1];
      if (!first) { event.preventDefault(); modal.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === modal)) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('focusin', keepFocus);
    modal.addEventListener('keydown', trapTab);
    return () => { document.removeEventListener('focusin', keepFocus); modal.removeEventListener('keydown', trapTab); };
  }, [activeSecondaryDialog]);
  const selectedOperation = copyDays.operations.find(op => op.operationId === selectedCopy?.operationId);
  const copyableDays = useMemo(() => new Set(sourceDays.filter(day => day.eligibleEntryCount > 0).map(day => day.workDate)), [sourceDays]);

  const openCopyDay = (sourceDate: string) => {
    // Only unresolved writes or unrefreshed successes require recovery before a
    // new confirmation. Completed receipts stay in the banner, not this dialog.
    const blocking = copyDays.operations.filter(op => op.state === 'uncertain' || op.needsRefresh);
    const existing = blocking.find(op => op.sourceDate === sourceDate) || blocking[0] ||
      copyDays.operations.find(op => op.sourceDate === sourceDate && (op.state === 'rejected' || op.state === 'notSubmitted'));
    setCopySelection({ sourceDate: existing?.sourceDate || sourceDate, operationId: existing?.operationId, accountKey });
  };

  useEffect(() => {
    let active = true;
    const loadLookups = async () => {
      const userViewOptions: TimeRequestOptions | undefined = actualIsAdmin ? { viewMode: 'User' } : undefined;
      try {
        const [profileData, customerData, projectData, projectTaskData] = await Promise.all([
          actualIsAdmin
            ? timeProfilesAPI.getProfiles(false, userViewOptions)
            : timeProfilesAPI.getProfiles(false),
          timeLookupsAPI.getCustomers(),
          timeLookupsAPI.getProjects(),
          timeLookupsAPI.getProjectTasks(),
        ]);
        if (!active) return;
        setProfiles(profileData);
        setCustomers(customerData);
        setProjects(projectData);
        setProjectTasks(projectTaskData);
        setEntryForm(prev => ({
          ...prev,
          timeProfileId: resolveEntryProfileId(profileData, user?.email, false, prev.timeProfileId),
        }));
      } catch {
        if (active) toast.error('Lookups could not be loaded');
      }
    };

    loadLookups();
    return () => { active = false; };
  }, [accountKey, actualIsAdmin, user?.email]);

  const formatProjectOption = (project: TimeProjectLookup) => `${project.customerTitle} - ${project.title}`;

  const entryProjects = useMemo(() => {
    if (!entryForm.customerId) {
      return projects;
    }

    return projects.filter(project => project.customerId === entryForm.customerId);
  }, [entryForm.customerId, projects]);

  const entryProjectTasks = useMemo(() => {
    if (!entryForm.projectId) {
      return [];
    }

    return projectTasks.filter(task => task.projectId === entryForm.projectId);
  }, [entryForm.projectId, projectTasks]);

  const formatProjectTaskOption = (task: TimeProjectTaskLookup) => task.projectId === entryForm.projectId
    ? task.title
    : `${task.customerTitle} - ${task.projectTitle} - ${task.title}`;

  const applyCustomerQuery = (value: string) => {
    const matchedCustomer = customers.find(customer => customer.title.toLowerCase() === value.trim().toLowerCase());
    setEntryForm(prev => {
      const customerId = matchedCustomer?.id || 0;
      const currentProjectStillMatches = customerId && projects.some(project => project.id === prev.projectId && project.customerId === customerId);

      return {
        ...prev,
        customerId,
        customerQuery: value,
        projectId: currentProjectStillMatches ? prev.projectId : 0,
        projectQuery: currentProjectStillMatches ? prev.projectQuery : '',
        projectTaskId: currentProjectStillMatches ? prev.projectTaskId : 0,
        projectTaskQuery: currentProjectStillMatches ? prev.projectTaskQuery : '',
      };
    });
  };

  const applyProjectQuery = (value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const matchedProject = entryProjects.find(project =>
      project.title.toLowerCase() === normalizedValue ||
      formatProjectOption(project).toLowerCase() === normalizedValue
    );

    setEntryForm(prev => ({
      ...prev,
      projectId: matchedProject?.id || 0,
      projectQuery: value,
      projectTaskId: 0,
      projectTaskQuery: '',
      customerId: matchedProject?.customerId || prev.customerId,
      customerQuery: matchedProject?.customerTitle || prev.customerQuery,
    }));
  };

  const applyProjectTaskQuery = (value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const matchedTask = entryProjectTasks.find(task =>
      task.title.toLowerCase() === normalizedValue ||
      formatProjectTaskOption(task).toLowerCase() === normalizedValue
    );

    setEntryForm(prev => ({
      ...prev,
      projectTaskId: matchedTask?.id || 0,
      projectTaskQuery: value,
    }));
  };

  const weekSections = useMemo(() => {
    return Array.from({ length: loadedWeekCount }, (_, weekOffset) => {
      const { startDate, endDate } = getDashboardWeekRange(currentWeekStart, weekOffset);
      return {
        key: startDate,
        title: weekOffset === 0 ? 'Current week' : weekOffset === 1 ? 'Previous week' : `${weekOffset} weeks ago`,
        dateRange: `${formatDate(startDate)} - ${formatDate(endDate)}`,
        days: Array.from({ length: 7 }, (_, index) => addDaysIso(endDate, -index)),
      };
    });
  }, [currentWeekStart, loadedWeekCount]);

  const dashboardDateRange = useMemo(() => `${formatDate(dashboardStartDate)} - ${formatDate(dashboardEndDate)}`, [dashboardEndDate, dashboardStartDate]);

  const entriesByDay = useMemo(() => {
    return weekEntries.reduce<Record<string, TimeEntry[]>>((result, entry) => {
      const key = entry.workDate.split('T')[0];
      result[key] = result[key] || [];
      result[key].push(entry);
      return result;
    }, {});
  }, [weekEntries]);

  const currentWeekTotalMinutes = useMemo(() => {
    const { startDate, endDate } = getDashboardWeekRange(currentWeekStart, 0);
    return weekEntries.reduce((total, entry) => {
      const workDate = entry.workDate.split('T')[0];
      return workDate >= startDate && workDate <= endDate
        ? total + entry.durationMinutes
        : total;
    }, 0);
  }, [currentWeekStart, weekEntries]);

  const openNewEntry = () => {
    setEditingEntry(null);
    setEntryForm({
      ...defaultEntryForm,
      timeProfileId: resolveEntryProfileId(profiles, user?.email, false),
    });
    setEntryFormOpen(true);
  };

  const openEntryEditor = (entry: TimeEntry) => {
    const duration = minutesToParts(entry.durationMinutes);
    const project = projects.find(item => item.id === entry.projectId);
    const customerId = entry.customerId || project?.customerId || 0;
    const customerTitle = entry.customerTitle || project?.customerTitle || '';
    setEditingEntry(entry);
    setEntryForm({
      timeProfileId: entry.timeProfileId,
      customerId,
      customerQuery: customerTitle,
      projectId: entry.projectId || 0,
      projectQuery: project ? formatProjectOption(project) : entry.customerTitle && entry.projectTitle ? `${entry.customerTitle} - ${entry.projectTitle}` : entry.projectTitle || '',
      projectTaskId: entry.projectTaskId || 0,
      projectTaskQuery: entry.projectTaskTitle || '',
      workDate: entry.workDate.split('T')[0],
      hours: duration.hours,
      minutes: duration.minutes,
      isBillable: entry.isBillable,
      description: entry.description || '',
    });
    setEntryFormOpen(true);
  };

  const copyEntry = (entry: TimeEntry) => {
    const duration = minutesToParts(entry.durationMinutes);
    const project = projects.find(item => item.id === entry.projectId);
    const customerId = entry.customerId || project?.customerId || 0;
    const customerTitle = entry.customerTitle || project?.customerTitle || '';
    setEditingEntry(null);
    setEntryForm({
      timeProfileId: entry.timeProfileId,
      customerId,
      customerQuery: customerTitle,
      projectId: entry.projectId || 0,
      projectQuery: project ? formatProjectOption(project) : entry.customerTitle && entry.projectTitle ? `${entry.customerTitle} - ${entry.projectTitle}` : entry.projectTitle || '',
      projectTaskId: entry.projectTaskId || 0,
      projectTaskQuery: entry.projectTaskTitle || '',
      workDate: entry.workDate.split('T')[0],
      hours: duration.hours,
      minutes: duration.minutes,
      isBillable: entry.isBillable,
      description: entry.description || '',
    });
    setEntryFormOpen(true);
  };

  const closeEntryEditor = () => {
    setEditingEntry(null);
    setEntryFormOpen(false);
    setEntryForm(defaultEntryForm);
  };

  const getDeleteEntryTitle = (entry: TimeEntry) => {
    const projectTitle = entry.projectTitle || 'Unassigned / internal';
    return `${formatDate(entry.workDate)} • ${projectTitle} • ${formatDurationMinutes(entry.durationMinutes)}`;
  };

  const getEntryProjectColor = (entry: TimeEntry) => entry.projectColor || projects.find(project => project.id === entry.projectId)?.color;

  const projectBadge = (label: string, color?: string) => (
    <span className="project-color-badge" style={{ '--project-color': color || '#64748b' } as React.CSSProperties}>
      <span className="project-color-dot" aria-hidden="true" />
      {label}
    </span>
  );

  const deleteEntry = async () => {
    if (!entryToDelete) {
      return;
    }

    try {
      await timeEntriesAPI.deleteEntry(entryToDelete.id);
      toast.success('Entry deleted');
      setEntryToDelete(null);
      await loadDashboard();
    } catch {
      toast.error('Entry could not be deleted');
    }
  };

  const handleEntrySubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (entrySaving) {
      return;
    }

    if (!entryForm.timeProfileId || !entryForm.projectId) {
      toast.error('Entry cannot be saved without a member and project');
      return;
    }

    const durationMinutes = partsToMinutes(entryForm.hours, entryForm.minutes);
    if (durationMinutes <= 0) {
      toast.error('Entry duration must be greater than 0');
      return;
    }

    try {
      const payload = {
        timeProfileId: entryForm.timeProfileId,
        projectId: entryForm.projectId,
        projectTaskId: entryForm.projectTaskId || undefined,
        workDate: entryForm.workDate,
        durationMinutes,
        isBillable: entryForm.isBillable,
        description: entryForm.description || undefined,
      };

      setEntrySaving(true);
      if (editingEntry) {
        await timeEntriesAPI.updateEntry(editingEntry.id, {
          ...payload,
          id: editingEntry.id,
        });
        toast.success('Entry updated');
      } else {
        await timeEntriesAPI.createEntry(payload);
        toast.success('Entry created');
      }
      closeEntryEditor();
      await loadDashboard();
    } catch {
      toast.error('Entry could not be saved');
    } finally {
      setEntrySaving(false);
    }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1 id="dashboard-title" tabIndex={-1}>Dashboard</h1>
          <p>{dashboardDateRange}</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={openNewEntry}>
          <i className="fa-solid fa-plus" aria-hidden="true" />
          New entry
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {!selectedCopy && copyDays.error && <p role="alert" className="alert alert-danger">{copyDays.error}</p>}
      {copyDays.operations.filter(op => op.operationId !== selectedCopy?.operationId).map(op => (
        <div className="alert copy-day-recovery" key={op.operationId}>
          <p role="status">{copyDayFeedback(op, dashboardStartDate, dashboardEndDate)}</p>
          {op.state === 'copied' && op.message && <p role="alert">{op.message}</p>}
          <button className="btn btn-secondary" type="button" onClick={() => setCopySelection({ sourceDate: op.sourceDate, operationId: op.operationId, accountKey })}
            aria-label={`Review copy ${formatDate(op.sourceDate)} to ${formatDate(op.destinationDate)}`}>Review copy</button>
        </div>
      ))}
      {selectedCopy && <CopyDayDialog key={`${accountKey}:${selectedCopy.sourceDate}`} sourceDate={selectedCopy.sourceDate}
        operation={selectedOperation} busy={!!selectedOperation && copyDays.busyIds.includes(selectedOperation.operationId)} error={copyDays.error}
        startDate={dashboardStartDate} endDate={dashboardEndDate}
        onSubmit={async destination => {
          const id = await copyDays.start(selectedCopy.sourceDate, destination, selectedOperation);
          if (id) setCopySelection(previous => previous?.accountKey === accountKey ? { ...previous, operationId: id } : previous);
        }}
        onRetry={() => { if (selectedOperation) void copyDays.retry(selectedOperation); }}
        onCheckStatus={() => { if (selectedOperation) void copyDays.reconcile(selectedOperation); }}
        onRetryRefresh={() => { if (selectedOperation) void copyDays.retryRefresh(selectedOperation); }}
        onClose={() => setCopySelection(null)}
        onAcknowledge={() => { if (selectedOperation && copyDays.acknowledge(selectedOperation)) setCopySelection(null); }} />}

      {isEntryFormOpen && entryAccountKey === accountKey && !selectedCopy && !entryToDelete && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeEntryEditor();
            }
          }}
        >
          <section ref={secondaryDialog} tabIndex={-1} className="time-modal entry-modal entry-modal-wide" role="dialog" aria-modal="true" aria-labelledby="dashboard-entry-dialog-title">
            <div className="time-modal-header">
              <h5 id="dashboard-entry-dialog-title" className="card-title">{editingEntry ? 'Edit entry' : 'New entry'}</h5>
              <button className="icon-button" type="button" onClick={closeEntryEditor} aria-label="Close entry editor">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <form className="time-form entry-form" onSubmit={handleEntrySubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="dashboard-entry-date">Date</label>
                  <DateInput
                    id="dashboard-entry-date"
                    className="form-control"
                    value={entryForm.workDate}
                    onChange={(value) => setEntryForm(prev => ({ ...prev, workDate: value }))}
                    required
                  />
                </div>
                <div className="duration-grid entry-duration-grid">
                  <div className="form-group">
                    <label className="form-label" htmlFor="dashboard-entry-hours">Hours</label>
                    <input
                      id="dashboard-entry-hours"
                      className="form-control"
                      type="number"
                      min="0"
                      max="24"
                      value={entryForm.hours}
                      onChange={(event) => setEntryForm(prev => ({ ...prev, hours: event.target.value === '' ? '' : Number(event.target.value) }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="dashboard-entry-minutes">Minutes</label>
                    <input
                      id="dashboard-entry-minutes"
                      className="form-control"
                      type="number"
                      min="0"
                      max="59"
                      value={entryForm.minutes}
                      onChange={(event) => setEntryForm(prev => ({ ...prev, minutes: event.target.value === '' ? '' : Number(event.target.value) }))}
                    />
                  </div>
                </div>
                <div className="entry-lookup-grid entry-lookup-grid-wide">
                  <div className="form-group entry-client-field">
                    <label className="form-label" htmlFor="dashboard-entry-client">Client</label>
                    <TimeSelect
                      inputId="dashboard-entry-client"
                      ariaLabel="Client"
                      options={customers.map(customer => ({ value: customer.id, label: customer.title }))}
                      value={entryForm.customerId || null}
                      onChange={(_, option) => applyCustomerQuery(option?.label || '')}
                      placeholder="Start typing a client"
                      noOptionsMessage="No clients"
                    />
                  </div>
                  <div className="form-group entry-project-field">
                    <label className="form-label" htmlFor="dashboard-entry-project">Project</label>
                    <TimeSelect
                      inputId="dashboard-entry-project"
                      ariaLabel="Project"
                      options={entryProjects.map(project => ({ value: project.id, label: formatProjectOption(project) }))}
                      value={entryForm.projectId || null}
                      onChange={(_, option) => applyProjectQuery(option?.label || '')}
                      placeholder={entryForm.customerId ? 'Start typing a project' : 'Select a client first'}
                      noOptionsMessage="No projects"
                    />
                  </div>
                  <div className="form-group entry-task-field">
                    <label className="form-label" htmlFor="dashboard-entry-task">Task <span className="optional-label">optional</span></label>
                    <TimeSelect
                      inputId="dashboard-entry-task"
                      ariaLabel="Task"
                      options={entryProjectTasks.map(task => ({ value: task.id, label: formatProjectTaskOption(task) }))}
                      value={entryForm.projectTaskId || null}
                      onChange={(_, option) => applyProjectTaskQuery(option?.label || '')}
                      placeholder={entryForm.projectId ? 'Start typing a task' : 'Select a project first'}
                      isClearable
                      isDisabled={!entryForm.projectId || entryProjectTasks.length === 0}
                      noOptionsMessage="No tasks"
                    />
                  </div>
                </div>
                <label className="toggle-row entry-toggle-row">
                  <input
                    type="checkbox"
                    checked={entryForm.isBillable}
                    onChange={(event) => setEntryForm(prev => ({ ...prev, isBillable: event.target.checked }))}
                  />
                  Billable
                </label>
                <div className="form-group entry-description-field">
                  <label className="form-label" htmlFor="dashboard-entry-description">Description</label>
                  <textarea
                    id="dashboard-entry-description"
                    className="form-control entry-description-control"
                    rows={5}
                    value={entryForm.description}
                    onChange={(event) => setEntryForm(prev => ({ ...prev, description: event.target.value }))}
                  />
                </div>
                <div className="form-actions">
                  <button className="btn btn-primary" type="submit" disabled={entrySaving} aria-busy={entrySaving}>
                    {entrySaving ? (
                      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    ) : (
                      <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
                    )}
                    {entrySaving ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={closeEntryEditor}>Cancel</button>
                </div>
              </form>
            </div>
          </section>
        </div>
      )}

      {entryToDelete && entryAccountKey === accountKey && !selectedCopy && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setEntryToDelete(null);
            }
          }}
        >
          <section ref={secondaryDialog} tabIndex={-1} className="time-modal" role="dialog" aria-modal="true" aria-labelledby="dashboard-delete-entry-dialog-title">
            <div className="time-modal-header">
              <h5 id="dashboard-delete-entry-dialog-title" className="card-title">Delete entry</h5>
              <button className="icon-button" type="button" onClick={() => setEntryToDelete(null)} aria-label="Close delete confirmation">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <p>Are you sure you want to delete this time entry?</p>
              <div className="delete-summary">
                <strong>{getDeleteEntryTitle(entryToDelete)}</strong>
                {entryToDelete.description && <span>{entryToDelete.description}</span>}
              </div>
              <div className="form-actions">
                <button className="btn btn-danger" type="button" onClick={deleteEntry}>
                  <i className="fa-solid fa-trash" aria-hidden="true" />
                  Delete entry
                </button>
                <button className="btn btn-secondary" type="button" onClick={() => setEntryToDelete(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      <div className="stat-grid">
        <div className="stat-card">
          <span>Total</span>
          <strong>{formatDurationMinutes(currentWeekTotalMinutes)}</strong>
        </div>
        <div className="stat-card">
          <span>Billable</span>
          <strong>{formatDurationMinutes(summary.billableMinutes)}</strong>
        </div>
        <div className="stat-card">
          <span>Non-billable</span>
          <strong>{formatDurationMinutes(summary.nonBillableMinutes)}</strong>
        </div>
        <div className="stat-card">
          <span>Entries</span>
          <strong>{summary.entryCount}</strong>
        </div>
      </div>

      <section className="card">
        <div className="card-header">
          <div>
            <h5 className="card-title">My weeks</h5>
            <span className="muted-text">Current and previous week shown by default</span>
          </div>
          <button className="btn btn-secondary" type="button" disabled={loadedWeekCount >= 57}
            onClick={() => setLoadedWeekCount(count => Math.min(count + 1, 57))}>
            <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
            Load previous week
          </button>
          {loadedWeekCount >= 57 && <span role="status" className="muted-text">Dashboard shows up to 57 complete weeks. Use Reports for older dates.</span>}
        </div>
        <div className="card-body">
          {weekSections.map(section => (
            <div className="week-day-list" key={section.key}>
              <div className="week-section-heading">
                <strong>{section.title}</strong>
                <span className="muted-text">{section.dateRange}</span>
              </div>
              {section.days.map(day => {
                const entries = entriesByDay[day] || [];
                const totalMinutes = entries.reduce((sum, entry) => sum + entry.durationMinutes, 0);
                const isCurrentDay = day === todayIso();

                return (
                  <div className="week-day-row" key={day}>
                    <div className={`week-day-header ${isCurrentDay ? 'current-day-title' : ''}`}>
                      <div>
                        <strong>{formatWeekday(day)}</strong>
                        <span>{formatDate(day)}</span>
                      </div>
                      <div className="copy-day-header-actions">
                        {copyableDays.has(day) && <button className="btn btn-secondary" type="button" disabled={loading}
                          onClick={() => openCopyDay(day)} aria-label={`Copy day ${formatDate(day)}`}>Copy day</button>}
                        <strong>{formatDurationMinutes(totalMinutes)}</strong>
                      </div>
                    </div>
                    {loading ? (
                      <div className="muted-row compact">Loading...</div>
                    ) : entries.length === 0 ? (
                      <div className="muted-row compact">No entries</div>
                    ) : (
                      <div className="day-entry-list">
                        {entries.map(entry => (
                          <article className="day-entry-row" key={entry.id} style={{ '--project-color': getEntryProjectColor(entry) || '#64748b' } as React.CSSProperties}>
                            <div className="day-entry-main">
                              <strong className="day-entry-title">{projectBadge(entry.projectTitle || 'Unassigned / internal', getEntryProjectColor(entry))}</strong>
                              <span className="day-entry-meta">{entry.projectTaskTitle ? `${entry.customerTitle || entry.timeProfileName} · ${entry.projectTaskTitle}` : entry.customerTitle || entry.timeProfileName}</span>
                              {entry.description && <span className="day-entry-description">{entry.description}</span>}
                            </div>
                            <div className="day-entry-side" role="group" aria-label="Entry summary and actions">
                              <div className="day-entry-summary">
                                <strong className="day-entry-duration">{formatDurationMinutes(entry.durationMinutes)}</strong>
                                <span className={`billing-badge ${entry.isBillable ? 'billable' : 'non-billable'}`}>
                                  {entry.isBillable ? 'Billable' : 'Non-billable'}
                                </span>
                              </div>
                              <div className="day-entry-actions" role="group" aria-label="Entry actions">
                                <button className="action-btn" type="button" onClick={() => openEntryEditor(entry)} aria-label={`Edit ${entry.description || entry.projectTitle || 'entry'}`}>
                                  <i className="fa-solid fa-pen" aria-hidden="true" />
                                </button>
                                <button className="action-btn" type="button" onClick={() => copyEntry(entry)} aria-label={`Copy ${entry.description || entry.projectTitle || 'entry'}`}>
                                  <i className="fa-solid fa-copy" aria-hidden="true" />
                                </button>
                                <button className="action-btn danger" type="button" onClick={() => setEntryToDelete(entry)} aria-label={`Delete ${entry.description || entry.projectTitle || 'entry'}`}>
                                  <i className="fa-solid fa-trash" aria-hidden="true" />
                                </button>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default Dashboard;
